import { apiFetch } from '@/lib/apiFetch';
import { throwApiError } from '@/lib/apiError';
import { handleUnauthorized } from './UserManagmentServices';

// Never wrap any of these in apiFetchWithRetry — every mutation here is a
// Razorpay order-create / payment-verify step (or a billing state change),
// same non-idempotent-from-Razorpay's-perspective rule coinPurchaseService.ts
// follows. The two plain GETs (plans, packages) are cheap and cached
// client-side via TanStack Query staleTime instead of relying on retry.

// --- Shared / cross-cutting types ---

export type SubscriptionTier = 'SILVER' | 'GOLD' | 'PLATINUM';
export type BillingCycle = 'MONTHLY' | 'YEARLY';
/**
 * Includes EXPIRED: GET /subscription deliberately falls back to the teacher's most recent
 * subscription when none is benefit-bearing, so an expired row does reach the client. Omitting it
 * here told callers a state they will actually receive is impossible.
 */
export type SubscriptionStatus = 'ACTIVE' | 'CANCELLED' | 'EXPIRED';

/**
 * Whether this subscription still carries its tier's benefits. CANCELLED counts -- benefits run
 * until endDate -- EXPIRED does not. Mirrors the backend's BENEFIT_BEARING set.
 */
export function isBenefitBearing(status: SubscriptionStatus | null | undefined): boolean {
  return status === 'ACTIVE' || status === 'CANCELLED';
}
export type AdBoostRunStatus = 'ACTIVE' | 'EXPIRED' | 'FORFEITED';

/** Why `renewable` is currently true on `SubscriptionStatusResponse` -- drives the F14 "Renew now" copy. */
export type RenewReason = 'CANCELLED_ENDING' | 'EXPIRED' | 'PAYMENT_HALTED' | null;

/** How a subscription period in the F13 history table came to an end. */
export type PeriodEndedBy =
  | 'ACTIVE'
  | 'CANCEL_PENDING'
  | 'UPGRADED'
  | 'DOWNGRADED'
  | 'CANCELLED'
  | 'EXPIRED'
  | 'DISPUTE_LOST';

/** Who/what triggered a subscription/ad-boost lifecycle event. */
export type SubscriptionEventSource = 'MANUAL' | 'AUTO' | 'SCHEDULED' | 'ADMIN' | 'SYSTEM';

// =====================================================================
// Subscription — /api/v1/teacher/subscription
// =====================================================================

/**
 * NOTE: this is the real teacher-facing DTO shape, verified against backend
 * source. Do NOT trust openapi.json's generated "SubscriptionPlanResponse"
 * schema — there is a Springdoc class-name collision with an unrelated, dead
 * student-facing class of the same name, and the dead one won the naming
 * collision in the generated spec for this one schema.
 */
export interface SubscriptionPlanResponse {
  planId: string;
  tier: SubscriptionTier;
  name: string;
  description: string;
  boostScore: number;
  monthlyPriceMinor: number;
  yearlyPriceMinor: number;
  monthlyDiscountPct: number;
  yearlyDiscountPct: number;
  isActive: boolean;
  /** Monthly bonus BMC Coins credited on this plan (F12). On a yearly subscription the coins are
   *  credited once, up front, as bonusCoinsPerMonth * 12 -- never twelve monthly drips. */
  bonusCoinsPerMonth: number;
  /** Percentage discount this tier gets on BMC Coin purchases/spend (F12). */
  coinDiscountPct: number;
  /** Offering types this tier is allowed to publish, e.g. ["ONE_TO_ONE", "GROUP"] (F12). */
  allowedOfferingTypes: string[];
  /** Max number of concurrently published offerings this tier allows (F12). */
  maxPublishedOfferings: number;
}

/** What a teacher with no subscription is allowed — the admin-configurable floor (ADR 129). */
export interface BaselineEntitlementsResponse {
  allowedOfferingTypes: string[];
  maxPublishedOfferings: number;
}

export interface SubscriptionPlansResponse {
  currency: string;
  plans: SubscriptionPlanResponse[];
  /**
   * The no-subscription floor. Drives the comparison table's "No Subscription" column, which must
   * NOT be rendered from the caller's own entitlements — those are the subscriber's own tier for
   * anyone who has one.
   */
  baseline?: BaselineEntitlementsResponse;
}

export interface PendingChangeResponse {
  targetTier: SubscriptionTier;
  targetPlanId: string;
  effectiveDate: string;
}

export interface CurrentSubscriptionResponse {
  tier: SubscriptionTier;
  planId: string;
  status: SubscriptionStatus;
  startDate: string;
  endDate: string | null;
  autoRenew: boolean;
  currency: string;
  billingCycle: BillingCycle;
}

/**
 * The teacher's Platinum free trial, exactly as the server resolved it.
 *
 * `daysRemaining` is server-computed and is the ONLY source for "N days left" -- never recompute it
 * from `endsAt` against the browser clock: the two can legitimately disagree (timezone, the
 * server's own day boundary), and the server's number is the one the emails and the backend's own
 * expiry job use.
 *
 * `startDate` and `endsAt` are date-only strings (`YYYY-MM-DD`). Format them with
 * `formatTrialDate()` from `@/lib/trialDate` -- `new Date('2027-01-15')` parses as UTC midnight and
 * renders as the 14th anywhere west of Greenwich.
 */
export interface TeacherTrialResponse {
  active: boolean;
  /** Date-only, `YYYY-MM-DD`. */
  startDate: string;
  /** Date-only, `YYYY-MM-DD`. The last day the trial's benefits apply. */
  endsAt: string;
  daysRemaining: number;
  /** How the trial was granted, e.g. `ORGANIC`. */
  grantSource: string;
}

/**
 * A purchase a trialling teacher has locked in that starts when the trial ends.
 *
 * The same row is ALSO returned in `subscription` (future-dated, already ACTIVE). Clients on a
 * trial must branch on this field: treating the `subscription` row as the current plan marks the
 * not-yet-started tier as "Your plan" while the teacher is still on their trial.
 */
export interface ScheduledSubscriptionResponse {
  subscriptionId: string;
  tier: SubscriptionTier;
  planId: string;
  billingCycle: BillingCycle;
  currency: string;
  /** Date-only, `YYYY-MM-DD` -- the day the paid subscription takes over from the trial. */
  startsOn: string;
  /** Date-only, `YYYY-MM-DD`. */
  endsOn: string;
}

export interface SubscriptionStatusResponse {
  subscription: CurrentSubscriptionResponse | null;
  pendingChange: PendingChangeResponse | null;
  adBoostEligible: boolean;
  boostScore: number;
  boostOverridden: boolean;
  /** F14 -- true when a "Renew now" action should be offered for the current subscription. */
  renewable: boolean;
  /** F14 -- reason `renewable` is true; drives the renew-copy variant. Null when not renewable. */
  renewReason: RenewReason;
  /** The teacher's Platinum free trial, or null when they have none / it has ended. */
  trial?: TeacherTrialResponse | null;
  /** A purchase scheduled to start when the trial ends -- see ScheduledSubscriptionResponse. */
  scheduledSubscription?: ScheduledSubscriptionResponse | null;
}

export interface CreateSubscriptionOrderPayload {
  planId: string;
  billingCycle: BillingCycle;
  /**
   * Explicit auto-renew consent (ADR-151/152), behind TEACHER_SUBSCRIPTION_AUTO_RENEW_CHOICE.
   * OMITTED entirely when the flag is off, which is what keeps today's behaviour byte-identical:
   * the backend then always creates a mandate. `false` asks for a one-time Order instead -- a
   * non-renewing purchase creates NO standing authority to charge.
   */
  autoRenew?: boolean;
}

/**
 * Exactly ONE of razorpaySubscriptionId / razorpayOrderId is populated, and which one says what
 * instrument paid for the purchase:
 *   - razorpaySubscriptionId -- a Razorpay Subscription mandate: a renewing subscribe, or a
 *     scheduled purchase.
 *   - razorpayOrderId -- a one-time Order: a tier change, a manual renewal, and (since ADR-152)
 *     a subscribe the teacher declined to auto-renew.
 */
export interface CreateSubscriptionOrderResponse {
  orderId: string;
  razorpaySubscriptionId: string | null;
  razorpayOrderId: string | null;
  razorpayKeyId: string;
  amountMinor: number;
  currency: string;
}

export interface VerifySubscriptionPaymentPayload {
  planId: string;
  billingCycle: BillingCycle;
  currency: string;
  /** Exactly one of razorpaySubscriptionId / razorpayOrderId -- whichever instrument paid. */
  razorpaySubscriptionId?: string;
  razorpayOrderId?: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

export interface CreateTierChangeOrderPayload {
  targetPlanId: string;
}

export interface VerifyTierChangePaymentPayload {
  targetPlanId: string;
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

export interface ScheduleTierDowngradePayload {
  targetPlanId: string;
}

export async function fetchSubscriptionPlans(accessToken: string): Promise<SubscriptionPlansResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/plans', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to fetch subscription plans: ${response.status}`);
  return response.json();
}

/**
 * The same catalogue as {@link fetchSubscriptionPlans}, fetched WITHOUT a token for the public
 * /pricing page. The endpoint is in the backend's `publicPaths`: it takes no JWT and resolves
 * currency from the request, so signed-out visitors get the identical admin-published rows.
 *
 * Deliberately a real fetch rather than a hardcoded price table. /pricing is a public commitment,
 * and any figure restated in the client goes stale silently the moment a plan row changes — which
 * is exactly the drift the coin-package fallback was already warned about.
 */
export async function fetchPublicSubscriptionPlans(): Promise<SubscriptionPlansResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/plans');
  if (!response.ok) await throwApiError(response, `Failed to fetch subscription plans: ${response.status}`);
  return response.json();
}

export async function fetchSubscriptionStatus(accessToken: string): Promise<SubscriptionStatusResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to fetch subscription status: ${response.status}`);
  return response.json();
}

export async function createSubscriptionOrder(
  accessToken: string,
  payload: CreateSubscriptionOrderPayload
): Promise<CreateSubscriptionOrderResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/order', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to create subscription order: ${response.status}`);
  return response.json();
}

export async function verifySubscriptionPayment(
  accessToken: string,
  payload: VerifySubscriptionPaymentPayload
): Promise<CurrentSubscriptionResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to verify subscription payment: ${response.status}`);
  return response.json();
}

export async function createTierChangeOrder(
  accessToken: string,
  payload: CreateTierChangeOrderPayload
): Promise<CreateSubscriptionOrderResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/tier-change/order', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to create tier-change order: ${response.status}`);
  return response.json();
}

export async function verifyTierChangePayment(
  accessToken: string,
  payload: VerifyTierChangePaymentPayload
): Promise<CurrentSubscriptionResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/tier-change/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to verify tier-change payment: ${response.status}`);
  return response.json();
}

/** Schedules a downgrade for the end of the current billing period — no payment involved. */
export async function scheduleTierDowngrade(
  accessToken: string,
  payload: ScheduleTierDowngradePayload
): Promise<CurrentSubscriptionResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/tier', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to schedule tier downgrade: ${response.status}`);
  return response.json();
}

export async function cancelPendingTierChange(accessToken: string): Promise<CurrentSubscriptionResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/pending-change', {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to cancel pending tier change: ${response.status}`);
  return response.json();
}

/** Cancel-at-period-end — subscription stays ACTIVE until endDate, then lapses. */
export async function cancelSubscription(accessToken: string): Promise<CurrentSubscriptionResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/cancel', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to cancel subscription: ${response.status}`);
  return response.json();
}

/**
 * Turn auto-renew OFF (AC#9-13, ADR-152). The subscription stays ACTIVE and keeps its tier, boost
 * and badge until endDate -- this is NOT a cancellation. ONE-WAY: there is no matching "turn on"
 * endpoint, by design, so no service function for one exists either.
 *
 * 404s while the backend's TEACHER_SUBSCRIPTION_AUTO_RENEW_CHOICE flag is off, so the UI must gate
 * the action on the frontend flag of the same name.
 */
export async function turnOffAutoRenew(accessToken: string): Promise<CurrentSubscriptionResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/auto-renew/off', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to turn off auto-renew: ${response.status}`);
  return response.json();
}

// =====================================================================
// Scheduled purchase during a trial — /api/v1/teacher/subscription/scheduled
// =====================================================================

/**
 * A trialling teacher cannot subscribe immediately -- POST /subscription/order 409s with
 * SUBSCRIPTION_SCHEDULING_REQUIRED while a trial is running. These three endpoints are the trial
 * equivalents: buy now, start when the trial ends.
 */
export interface CreateScheduledOrderPayload {
  planId: string;
  billingCycle: BillingCycle;
  /** Optional -- the server defaults to the plan's currency when omitted. */
  currency?: string;
}

export interface CreateScheduledOrderResponse {
  orderId: string;
  /** Always a Razorpay Subscription (the mandate only starts charging on `firstChargeOn`). */
  razorpaySubscriptionId: string;
  razorpayKeyId: string;
  amountMinor: number;
  currency: string;
  /** Date-only, `YYYY-MM-DD` -- the trial end date the first charge is scheduled for. */
  firstChargeOn: string;
}

export async function createScheduledOrder(
  accessToken: string,
  payload: CreateScheduledOrderPayload
): Promise<CreateScheduledOrderResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/scheduled/order', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to create scheduled subscription order: ${response.status}`);
  return response.json();
}

/** Verifies the mandate signature; the returned subscription is future-dated to the trial end. */
export async function verifyScheduledPayment(
  accessToken: string,
  payload: VerifySubscriptionPaymentPayload
): Promise<CurrentSubscriptionResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/scheduled/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to verify scheduled subscription payment: ${response.status}`);
  return response.json();
}

/** Cancels a not-yet-started scheduled purchase. The trial itself is untouched. */
export async function cancelScheduledSubscription(accessToken: string): Promise<SubscriptionStatusResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/scheduled', {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to cancel scheduled subscription: ${response.status}`);
  return response.json();
}

// =====================================================================
// Renew — /api/v1/teacher/subscription/renew (F14)
// =====================================================================

export interface VerifyRenewalPayload {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

/** Starts a manual renew — an Orders API one-off charge that extends the existing subscription's period. */
export async function createRenewOrder(accessToken: string): Promise<CreateSubscriptionOrderResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/renew', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to create renewal order: ${response.status}`);
  return response.json();
}

export async function verifyRenewPayment(
  accessToken: string,
  payload: VerifyRenewalPayload
): Promise<CurrentSubscriptionResponse> {
  const response = await apiFetch('/api/v1/teacher/subscription/renew/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to verify renewal payment: ${response.status}`);
  return response.json();
}

// =====================================================================
// History — /api/v1/teacher/subscription/history (F13)
// =====================================================================

/**
 * A server-derived, immutable slice of a teacher's subscription timeline. The
 * server folds the raw event stream into these periods precisely so clients
 * don't each reimplement that derivation -- render this straight, don't
 * recompute tier/date-range/endedBy from `events` client-side.
 */
export interface TeacherSubscriptionPeriodResponse {
  periodId: string;
  tier: SubscriptionTier;
  billingCycle: BillingCycle;
  startDate: string;
  /** Null while the period is still ongoing (current ACTIVE or CANCEL_PENDING period). */
  endDate: string | null;
  endedBy: PeriodEndedBy;
}

export interface TeacherSubscriptionEventResponse {
  id: string;
  eventType: string;
  occurredAt: string;
  source: SubscriptionEventSource;
  tier: string | null;
  previousTier: string | null;
  billingCycle: BillingCycle | null;
  periodStartDate: string | null;
  periodEndDate: string | null;
  adBoostDurationDays: number | null;
  relatedSubscriptionId: string | null;
  relatedPaymentEventId: string | null;
  relatedAdBoostPurchaseId: string | null;
  detail: string | null;
}

export interface PagedTeacherSubscriptionEvents {
  content: TeacherSubscriptionEventResponse[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
}

export interface TeacherSubscriptionHistoryResponse {
  periods: TeacherSubscriptionPeriodResponse[];
  events: PagedTeacherSubscriptionEvents;
}

export async function fetchSubscriptionHistory(
  accessToken: string,
  page: number,
  size: number
): Promise<TeacherSubscriptionHistoryResponse> {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  const response = await apiFetch(`/api/v1/teacher/subscription/history?${params.toString()}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to fetch subscription history: ${response.status}`);
  return response.json();
}

// =====================================================================
// Ad boost — /api/v1/teacher/ad-boost
// =====================================================================

export interface AdBoostPackageResponse {
  packageId: string;
  durationDays: 1 | 3 | 7;
  boostScore: number;
  priceMinor: number;
  isActive: boolean;
}

export interface AdBoostPackagesResponse {
  currency: string;
  packages: AdBoostPackageResponse[];
}

export interface CurrentAdBoostResponse {
  boostScoreApplied: number;
  status: AdBoostRunStatus;
  startsAt: string;
  expiresAt: string;
  autoRenew: boolean;
}

export interface AdBoostStatusResponse {
  adBoost: CurrentAdBoostResponse | null;
  eligible: boolean;
  boostScore: number;
  boostOverridden: boolean;
}

export interface CreateAdBoostOrderPayload {
  packageId: string;
  autoRenew: boolean;
}

export interface CreateAdBoostOrderResponse {
  orderId: string;
  razorpayOrderId: string;
  razorpayKeyId: string;
  amountMinor: number;
  currency: string;
}

export interface VerifyAdBoostPaymentPayload {
  packageId: string;
  currency: string;
  autoRenew: boolean;
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

export interface UpdateAdBoostAutoRenewPayload {
  autoRenew: boolean;
}

export async function fetchAdBoostPackages(accessToken: string): Promise<AdBoostPackagesResponse> {
  const response = await apiFetch('/api/v1/teacher/ad-boost/packages', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to fetch ad-boost packages: ${response.status}`);
  return response.json();
}

export async function fetchAdBoostStatus(accessToken: string): Promise<AdBoostStatusResponse> {
  const response = await apiFetch('/api/v1/teacher/ad-boost', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to fetch ad-boost status: ${response.status}`);
  return response.json();
}

export async function createAdBoostOrder(
  accessToken: string,
  payload: CreateAdBoostOrderPayload
): Promise<CreateAdBoostOrderResponse> {
  const response = await apiFetch('/api/v1/teacher/ad-boost/order', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to create ad-boost order: ${response.status}`);
  return response.json();
}

export async function verifyAdBoostPayment(
  accessToken: string,
  payload: VerifyAdBoostPaymentPayload
): Promise<AdBoostStatusResponse> {
  const response = await apiFetch('/api/v1/teacher/ad-boost/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to verify ad-boost payment: ${response.status}`);
  return response.json();
}

export async function updateAdBoostAutoRenew(
  accessToken: string,
  payload: UpdateAdBoostAutoRenewPayload
): Promise<AdBoostStatusResponse> {
  const response = await apiFetch('/api/v1/teacher/ad-boost/auto-renew', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) await throwApiError(response, `Failed to update ad-boost auto-renew: ${response.status}`);
  return response.json();
}
